import { 
  FaWifi, 
  FaSnowflake, 
  FaParking, 
  FaWater, 
  FaTv, 
  FaDice,
  FaUtensils,
  FaShower,
  FaLock
} from 'react-icons/fa';
import { motion } from 'framer-motion';
import SectionHeading from '../ui/SectionHeading';
import Card from '../ui/Card';

const amenitiesList = [
  {
    icon: <FaWifi />,
    title: 'Free WiFi',
    description: 'High-speed internet connection throughout the property'
  },
  {
    icon: <FaSnowflake />,
    title: 'Air Conditioning',
    description: 'Stay cool with air conditioning in all bedrooms and living areas'
  },
  {
    icon: <FaParking />,
    title: 'Free Parking',
    description: 'Secure on-premises parking for up to 4 vehicles'
  },
  {
    icon: <FaUtensils />,
    title: 'Fully Equipped Kitchen',
    description: 'Modern appliances and all necessary cookware for meal preparation'
  },
  {
    icon: <FaWater />,
    title: 'Washing Machine',
    description: 'Complimentary use of washing machine for longer stays'
  },
  {
    icon: <FaShower />,
    title: 'Hot Water',
    description: 'Enjoy hot showers in all bathrooms with reliable water heaters'
  },
  {
    icon: <FaTv />,
    title: 'Smart TV',
    description: 'Smart TV with Netflix and other streaming services'
  },
  {
    icon: <FaDice />,
    title: 'Board Games',
    description: 'Selection of board games and cards for family entertainment'
  },
  {
    icon: <FaLock />,
    title: 'Self Check-in',
    description: 'Convenient self check-in with secure key lockbox'
  }
];

export default function Amenities() {
  // Animation variants
  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12,
        delayChildren: 0.2,
      },
    },
  };

  const item = {
    hidden: { opacity: 0, y: 25 },
    show: { 
      opacity: 1, 
      y: 0,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 12,
      },
    },
  };
  
  const iconAnimation = {
    rest: { scale: 1 },
    hover: { 
      scale: 1.15,
      rotate: [0, 10, -10, 0],
      transition: { duration: 0.6, ease: "easeInOut" }
    }
  };
  
  const cardAnimation = {
    rest: { 
      backgroundColor: "rgba(255, 255, 255, 1)",
      y: 0,
      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)"
    },
    hover: { 
      backgroundColor: "rgba(239, 246, 255, 0.7)",
      y: -5,
      boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
      transition: { duration: 0.3, ease: "easeOut" }
    }
  };

  return (
    <>
      <SectionHeading 
        title="Amenities" 
        subtitle="Everything you need for a comfortable and enjoyable stay"
      />
      
      <motion.div 
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8"
        variants={container}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-100px" }}
      >
        {amenitiesList.map((amenity, index) => (
          <motion.div key={index} variants={item}>
            <motion.div
              initial="rest"
              whileHover="hover"
              animate="rest"
              variants={cardAnimation}
              className="h-full rounded-xl overflow-hidden"
            >
              <Card className="h-full flex flex-col items-center text-center p-8 border-0 shadow-none">
                <motion.div 
                  className="mb-5 h-16 w-16 flex items-center justify-center rounded-full bg-gradient-to-br from-blue-400 to-blue-600 text-white shadow-md"
                  variants={iconAnimation}
                >
                  <div className="text-2xl">
                    {amenity.icon}
                  </div>
                </motion.div>
                
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{amenity.title}</h3>
                
                <p className="text-gray-600">{amenity.description}</p>
                
                <motion.div 
                  className="w-16 h-1 bg-gradient-to-r from-blue-400 to-blue-600 rounded-full mt-5 opacity-0"
                  variants={{
                    rest: { opacity: 0, width: "16px" },
                    hover: { opacity: 1, width: "64px", transition: { duration: 0.3 } }
                  }}
                />
              </Card>
            </motion.div>
          </motion.div>
        ))}
      </motion.div>
    </>
  );
}
